import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  structuredData?: object[];
  breadcrumbs?: Array<{ name: string; url: string }>;
}

/**
 * SEO Component - Manages all SEO-related meta tags, structured data, and canonical URLs
 * Implements best practices for Swedish market targeting
 */
const SEO = ({
  title = "ADGS - Digitalisera er framtid",
  description = "Personlig expertis inom mjukvara och digitalisering. Vi hjälper företag att effektivisera verksamheten och bygga skräddarsydda digitala lösningar.",
  canonicalUrl = "https://adgs.se/",
  ogImage = "https://adgs.se/hero-background.jpg",
  structuredData = [],
  breadcrumbs
}: SEOProps) => {
  const fullTitle = title === "ADGS - Digitalisera er framtid" ? title : `${title} | ADGS`;

  // Base Organization Schema (always included)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://adgs.se/#organization",
    name: "ADGS",
    legalName: "ADGS AB",
    url: "https://adgs.se",
    logo: {
      "@type": "ImageObject",
      url: "https://adgs.se/favicon.svg"
    },
    description: "Mjukvaruutveckling, Webb & Rådgivning för svenska företag",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Malmö",
      addressCountry: "SE"
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "info@adgs.se",
      contactType: "customer service",
      availableLanguage: ["Swedish"]
    },
    sameAs: [
      "https://www.linkedin.com/company/spasovic-gonzalez"
    ],
    founder: [
      {
        "@type": "Person",
        name: "Adrian Gonzalez",
        jobTitle: "CEO",
        email: "adrian@adgs.se",
        url: "https://www.linkedin.com/in/adrian-gonzalez85/"
      },
      {
        "@type": "Person",
        name: "Dejan Spasovic",
        jobTitle: "CTO",
        email: "dejan@adgs.se",
        url: "https://www.linkedin.com/in/dejan-spasovic-0229948b/"
      }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://adgs.se/#localbusiness",
    name: "ADGS",
    image: "https://adgs.se/hero-background.jpg",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Malmö",
      addressRegion: "Skåne",
      addressCountry: "SE"
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "55.6050",
      longitude: "13.0038"
    },
    url: "https://adgs.se",
    telephone: "+46",
    email: "info@adgs.se",
    priceRange: "$$",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00"
    }
  };

  // WebSite Schema with SearchAction
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://adgs.se/#website",
    url: "https://adgs.se",
    name: "ADGS",
    description: "Mjukvaruutveckling, Webb & Rådgivning",
    publisher: {
      "@id": "https://adgs.se/#organization"
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://adgs.se/services?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    },
    inLanguage: "sv-SE"
  };

  // Breadcrumb Schema (if breadcrumbs provided)
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  } : null;

  // Combine all schemas
  const allSchemas = [
    organizationSchema,
    localBusinessSchema,
    websiteSchema,
    breadcrumbSchema,
    ...structuredData
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language and Regional Targeting */}
      <html lang="sv" />
      <link rel="alternate" hrefLang="sv-SE" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="sv_SE" />
      <meta property="og:site_name" content="ADGS" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
